import {
  ChatActiveArtifactContextschemaKindEnum,
  TopologyOverlayBaseschemaSourceEnum,
  TopologyOverlayBaseschemaTimeModeEnum
} from '@app/types/sdk';
import type {
  ArtifactContentTopologyschema,
  ChatActiveArtifactContextschema,
  TopologyOverlayBaseschema,
  TopologyV2ResolveRequest
} from '@app/types/sdk';

export type TopologyArtifactContent = ArtifactContentTopologyschema;
export type TopologyResolveRequest = TopologyV2ResolveRequest;
export type TopologyArtifactSurface = ChatActiveArtifactContextschema['surface'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const nonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const isPositiveInteger = (value: unknown): value is number => Number.isInteger(value) && Number(value) > 0;

const sanitizeParams = (params: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(params) || Object.keys(params).length === 0) {
    return undefined;
  }
  return { ...params };
};

const sanitizeStringSet = (values: Iterable<unknown> | undefined): Set<string> | undefined => {
  if (!values) {
    return undefined;
  }
  const out = new Set<string>();
  for (const value of values) {
    const item = nonEmptyString(value);
    if (item) {
      out.add(item);
    }
  }
  return out.size > 0 ? out : undefined;
};

const sanitizeLimits = (limits: unknown): TopologyV2ResolveRequest['limits'] | undefined => {
  if (!isRecord(limits)) {
    return undefined;
  }
  const out: NonNullable<TopologyV2ResolveRequest['limits']> = {};
  if (isPositiveInteger(limits.limitNodes)) {
    out.limitNodes = limits.limitNodes;
  }
  if (isPositiveInteger(limits.limitEdges)) {
    out.limitEdges = limits.limitEdges;
  }
  const rootNodeId = nonEmptyString(limits.rootNodeId);
  if (rootNodeId) {
    out.rootNodeId = rootNodeId;
  }
  if (isPositiveInteger(limits.hops)) {
    out.hops = limits.hops;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

export const isTopologyArtifactContent = (value: unknown): value is TopologyArtifactContent => {
  if (!isRecord(value) || value.type !== 'topology' || !isRecord(value.binding)) {
    return false;
  }
  const binding = value.binding;
  return Boolean(nonEmptyString(binding.viewId) && isRecord(binding.scope));
};

const asDate = (value: unknown): Date | undefined => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  const text = nonEmptyString(value);
  if (!text) {
    return undefined;
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const buildTopologyResolveRequest = (content: TopologyArtifactContent): TopologyResolveRequest => {
  if (!isTopologyArtifactContent(content)) {
    throw new Error('Invalid topology artifact content.');
  }
  const binding = content.binding;
  if (binding.source !== 'topology-service') {
    throw new Error('Topology artifact source must be topology-service.');
  }
  const scopeType = nonEmptyString(binding.scope?.scopeType);
  const scopeId = nonEmptyString(binding.scope?.scopeId);
  if (!scopeType || !scopeId) {
    throw new Error('Topology artifact scope is missing.');
  }
  if (binding.timeMode !== 'fixed' && binding.timeMode !== 'current') {
    throw new Error('Topology artifact timeMode must be fixed or current.');
  }

  const request: TopologyResolveRequest = {
    scopeType: scopeType as TopologyResolveRequest['scopeType'],
    scopeId
  };
  const params = sanitizeParams(binding.params);
  if (params) {
    request.params = params;
  }
  const limits = sanitizeLimits(binding.limits);
  if (limits) {
    request.limits = limits;
  }
  if (binding.timeMode === 'fixed') {
    const asOf = asDate(binding.asOf);
    if (!asOf) {
      throw new Error('Fixed topology artifacts require binding.asOf.');
    }
    request.asOf = asOf;
  }
  return request;
};

export const buildTopologyOverlayBase = (content: TopologyArtifactContent): TopologyOverlayBaseschema => {
  if (!isTopologyArtifactContent(content)) {
    throw new Error('Invalid topology artifact content.');
  }
  const binding = content.binding;
  if (binding.source !== 'topology-service') {
    throw new Error('Topology artifact source must be topology-service.');
  }
  if (binding.timeMode !== 'fixed' && binding.timeMode !== 'current') {
    throw new Error('Topology artifact timeMode must be fixed or current.');
  }

  const base: TopologyOverlayBaseschema = {
    source: TopologyOverlayBaseschemaSourceEnum.TopologyService,
    viewId: binding.viewId,
    scope: binding.scope,
    timeMode:
      binding.timeMode === 'current'
        ? TopologyOverlayBaseschemaTimeModeEnum.Current
        : TopologyOverlayBaseschemaTimeModeEnum.Fixed
  };
  const params = sanitizeParams(binding.params);
  if (params) {
    base.params = params;
  }
  const limits = sanitizeLimits(binding.limits);
  if (limits) {
    base.limits = limits;
  }
  if (binding.timeMode === 'fixed') {
    const asOf = asDate(binding.asOf);
    if (!asOf) {
      throw new Error('Fixed topology artifacts require binding.asOf.');
    }
    base.asOf = asOf;
  }
  return base;
};

export const buildTopologyStreamUrl = (baseUrl: string, request: TopologyResolveRequest, fromSeq?: number): string => {
  const url = new URL(baseUrl);
  url.searchParams.set('scopeType', request.scopeType);
  url.searchParams.set('scopeId', request.scopeId);
  if (request.params && Object.keys(request.params).length > 0) {
    url.searchParams.set('params', JSON.stringify(request.params));
  }
  if (request.limits && Object.keys(request.limits).length > 0) {
    url.searchParams.set('limits', JSON.stringify(request.limits));
  }
  if (request.asOf instanceof Date && !Number.isNaN(request.asOf.getTime())) {
    url.searchParams.set('asOf', request.asOf.toISOString());
  }
  if (typeof fromSeq === 'number' && Number.isFinite(fromSeq) && fromSeq > 0) {
    url.searchParams.set('fromSeq', String(Math.floor(fromSeq)));
  }
  return url.toString();
};

export const topologyArtifactKey = (content: TopologyArtifactContent): string => {
  const binding = content.binding;
  return JSON.stringify({
    source: binding.source,
    viewId: binding.viewId,
    scope: binding.scope,
    params: binding.params ?? {},
    limits: binding.limits ?? {},
    timeMode: binding.timeMode,
    asOf: binding.asOf ?? null
  });
};

const comparableDate = (value: unknown): string | null => {
  const date = asDate(value);
  return date ? date.toISOString() : null;
};

const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJson(item)).join(',')}]`;
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

export const topologyOverlayBaseMatches = (
  content: TopologyArtifactContent,
  candidate: TopologyOverlayBaseschema | undefined | null
): boolean => {
  if (!candidate) {
    return false;
  }
  const expected = buildTopologyOverlayBase(content);
  return (
    candidate.source === expected.source &&
    candidate.viewId === expected.viewId &&
    candidate.scope?.scopeType === expected.scope?.scopeType &&
    candidate.scope?.scopeId === expected.scope?.scopeId &&
    candidate.timeMode === expected.timeMode &&
    stableJson(candidate.params ?? {}) === stableJson(expected.params ?? {}) &&
    stableJson(candidate.limits ?? {}) === stableJson(expected.limits ?? {}) &&
    comparableDate(candidate.asOf) === comparableDate(expected.asOf)
  );
};

export const buildTopologyActiveArtifactContext = ({
  artifactId,
  topology,
  surface,
  selectedNodeIds,
  selectedEdgeIds,
  activeLayerIds,
  revision
}: {
  artifactId?: string;
  topology: TopologyArtifactContent;
  surface?: TopologyArtifactSurface;
  selectedNodeIds?: Iterable<unknown>;
  selectedEdgeIds?: Iterable<unknown>;
  activeLayerIds?: Iterable<unknown>;
  revision?: number;
}): ChatActiveArtifactContextschema => {
  const binding = topology.binding;
  const context: ChatActiveArtifactContextschema = {
    artifactId,
    kind: ChatActiveArtifactContextschemaKindEnum.Topology,
    title: topology.title,
    surface,
    viewId: binding.viewId,
    scope: binding.scope
  };
  const nodes = sanitizeStringSet(selectedNodeIds);
  if (nodes) {
    context.selectedNodeIds = nodes;
  }
  const edges = sanitizeStringSet(selectedEdgeIds);
  if (edges) {
    context.selectedEdgeIds = edges;
  }
  const layers = sanitizeStringSet(activeLayerIds);
  if (layers) {
    context.activeLayerIds = layers;
  }
  if (typeof revision === 'number' && Number.isFinite(revision) && revision >= 0) {
    context.revision = Math.floor(revision);
  }
  return context;
};
